import React, { Dispatch, SetStateAction, useState } from 'react'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import 'main/config/yup'
import { cpfMask, dateMask } from 'presentation/utils/masks'
import Heading from 'presentation/shared/components/Heading'
import SupportText from 'presentation/shared/components/SupportText'
import NotFountPacientModal from 'presentation/secretary/components/NotFoundPacientModal'
import * as S from './styles'
import ActualPage from 'presentation/shared/components/ActualPage'
import { useFormik, useFormikContext } from 'formik'
import { AddSurgicalOrderFormValues } from '../index'
import { validateCpf } from 'presentation/utils/validators/cpf-validator'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import SearchPatientByNameModal from 'presentation/hospital/components/SearchPatientByName'
import { Patient } from 'domain/entities/patient-model'
import { SearchPatientInGateway } from 'domain/usecases/patient/search-patient-in-gateway'
import moment from 'moment'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import { dateInputFormat } from 'presentation/utils/date-format'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'

type Props = {
  findPatient?: (cpf: string) => void
  goNext?: () => void
  goBack?: () => void
  handleNotFoundPatientModal?: boolean
  setCreatePatientMode: Dispatch<SetStateAction<boolean>>
  searchPatient: SearchPatientInGateway
  handleClosePacientModal: () => void
} & WithLoadingProps

//add patient modal
const SelectSurgicalOrderPatient = WithLoading(
  ({
    findPatient,
    goNext,
    goBack,
    setCreatePatientMode,
    handleClosePacientModal,
    searchPatient,
    handleNotFoundPatientModal,
    setIsLoading
  }: Props) => {
    const [showFindModal, setShowFindModal] = useState<boolean>(false)
    const [findedPatients, setFindedPatients] = useState<Patient[]>([])

    const searchPatientByCpf = () => {
      if (validateCpf(formik.values.cpf)) {
        !!findPatient && findPatient(formik.values.cpf.replace(/\D+/g, ''))
      } else {
        toast.error('CPF inválido')
      }
    }

    const searchPatientByNameAndBday = async () => {
      const date = moment(formik.values.birthday, 'DD/MM/YYYY')
      date.subtract(date.parseZone().utcOffset(), 'minutes')

      try {
        setIsLoading(true)
        setFindedPatients(
          await searchPatient
            .load({
              name: formik.values.name,
              bday: date.toISOString()
            } as any)
            .then((res) => {
              return res.data.map((p: any) => ({
                ...p,
                cpf: p.user?.cpf ?? p.cpf,
                email: p.user?.email ?? p.email,
                phone: p.user?.phone ?? p.phone,
                maritalStatus: p.maritalStatus ?? p.marital_status
              }))
            })
        )
        setShowFindModal(true)
      } catch (e: any) {
        toast.error('Paciente não encontrado')
      } finally {
        setIsLoading(false)
      }
    }

    const { values, setFieldValue } =
      useFormikContext<AddSurgicalOrderFormValues>()

    const addPatient = () => {
      setFieldValue('patient', undefined)
      setCreatePatientMode(true)
      goNext?.()
    }

    const formik = useFormik({
      initialValues: {
        cpf: '',
        birthday: '',
        name: '',
        searchType: 'cpf'
      },
      validateOnMount: true,
      validationSchema: () =>
        yup.lazy((values) => {
          const isCPF = values.searchType === 'cpf'

          return isCPF ? cpfValidationSchema : nameValidationSchema
        }),
      onSubmit: () => undefined
    })

    const changeSearchMode = () => {
      formik.setFieldValue(
        'searchType',
        formik.values.searchType === 'cpf' ? 'name' : 'cpf'
      )
    }

    return (
      <S.Wrapper>
        <ActualPage text="Voltar" onClick={goBack} />
        <Heading size="xlarge" color="primary" as="h1">
          Novo pedido cirúrgico
        </Heading>
        <SupportText style={{ marginBottom: '36px' }}>
          Busque um paciente pelo CPF ou nome
        </SupportText>
        <S.AddPatient>
          <Heading as="h5" color="gray" size="medium">
            Paciente
          </Heading>
        </S.AddPatient>
        <SupportText
          color="lightGray"
          style={{ marginTop: '2px', marginBottom: '8px' }}
        >
          {formik.values.searchType === 'cpf'
            ? 'Informe o CPF para o prosseguimento do pedido'
            : 'Informe o nome e a data de nascimento do paciente'}
        </SupportText>
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
          }}
        >
          <S.Inputs>
            {formik.values.searchType === 'cpf' ? (
              <>
                <TextFieldNew
                  label="CPF"
                  name="cpf"
                  id="cpf"
                  mask={cpfMask}
                  onChange={formik.handleChange('cpf')}
                  defaultValue={values.patient?.cpf}
                  placeholder="Inserir CPF..."
                  icon={<SearchIcon />}
                  iconPosition="insideRight"
                  onIconClick={searchPatientByCpf}
                  iconIsButton
                  required
                  data-testid={`patient-cpf-input`}
                />
                <TextFieldNew
                  label="Paciente"
                  disabled
                  defaultValue={values.patient?.name}
                  placeholder="Nome do paciente"
                  data-testid={`patient-name-disabled-input`}
                />
              </>
            ) : (
              <>
                <TextFieldNew
                  label="Paciente"
                  defaultValue={values.patient?.name}
                  placeholder="Nome do paciente"
                  onChange={formik.handleChange('name')}
                  // icon={<SearchIcon />}
                  // iconPosition="insideRight"
                  // iconMargin="54px"
                  data-testid={`patient-name-input`}
                />
                <TextFieldNew
                  label="Data de nascimento"
                  name="birthday"
                  id="birthday"
                  mask={dateMask}
                  onChange={formik.handleChange('birthday')}
                  defaultValue={formik.values.birthday}
                  placeholder="Data de nascimento"
                  required
                  data-testid={`patient-birthday-input`}
                />

                <ButtonNew
                  size="large"
                  noPadding
                  onClick={searchPatientByNameAndBday}
                  disabled={!!(formik.errors.name || formik.errors.birthday)}
                  data-testid={`patient-birthday-button`}
                  icon={<SearchIcon width="24px" height="24px" />}
                  iconPosition="center"
                  style={{
                    width: '48px'
                  }}
                >
                  {/* Buscar */}
                </ButtonNew>
              </>
            )}
          </S.Inputs>
          <SupportText
            color="primary"
            style={{
              cursor: 'pointer',
              alignSelf: 'flex-start',
              fontWeight: 500,
              marginTop: '16px'
            }}
            onClick={changeSearchMode}
            data-testid={`change-search-patient-mode-button`}
          >
            {formik.values.searchType === 'cpf'
              ? 'Buscar por nome'
              : 'Buscar por CPF'}
          </SupportText>
        </div>
        <S.Buttons>
          <ButtonNew
            size="large"
            outlined
            onClick={addPatient}
            data-testid={`invite-patient-button`}
          >
            Cadastrar paciente
          </ButtonNew>
          <ButtonNew
            size="large"
            style={{
              width: '100%',
              maxWidth: '320px'
            }}
            disabled={!values.patient?.patient_id}
            onClick={() => {
              setCreatePatientMode(false)
              goNext?.()
            }}
            type="button"
            data-testid={`select-patient-next-button`}
          >
            Próximo
          </ButtonNew>
        </S.Buttons>
        {showFindModal && (
          <SearchPatientByNameModal
            close={() => setShowFindModal(false)}
            patients={findedPatients}
            selectPatient={(patient) => {
              const normalizedPatient = {
                ...patient,
                cpf: patient.cpf,
                email: patient.email,
                phone: patient.phone,
                birthday: dateInputFormat(patient.birthday),
                maritalStatus: patient.maritalStatus,
                landlinePhone: patient.landlinePhone
              }
              const isPatientHospitalized =
                normalizedPatient.internation?.paciente_internado ?? false
              setFieldValue('patientHospitalized', isPatientHospitalized)
              setFieldValue('patient', normalizedPatient)
            }}
          />
        )}

        {handleNotFoundPatientModal && (
          <NotFountPacientModal
            show={true}
            action={addPatient}
            close={handleClosePacientModal}
          />
        )}
      </S.Wrapper>
    )
  }
)

export default SelectSurgicalOrderPatient

const cpfValidationSchema = yup.object().shape({
  cpf: yup.string().cpf().required()
})

const nameValidationSchema = yup.object().shape({
  birthday: yup.date().format('DD/MM/YYYY', true).required(),
  name: yup.string().required()
})
